import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useMemo, useState } from 'react';
import { AlertTriangle, RotateCcw, Save } from 'lucide-react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import {
  AI_ROLE_HINTS, AI_ROLE_LABELS,
  type AiModelDto, type AiProviderDto, type AiRoleId,
} from '../../../services/aiControlApi';
import {
  modelOptions, providerOptions, roleDiff, validateRoleDraft, type RoleDraft,
} from './routingDraft';

interface Props {
  roleId: AiRoleId;
  original: RoleDraft;
  draft: RoleDraft;
  providers: AiProviderDto[];
  models: AiModelDto[];
  saving: boolean;
  onChange: (next: RoleDraft) => void;
  onSave: () => void;
  onReset: () => void;
}

const FIELD_LABEL = 'text-[10px] uppercase tracking-wide text-gray-500 font-semibold block mb-1';
const NUM_INPUT =
  'w-full px-3 py-2 min-h-[44px] text-sm border border-stone-200 rounded-lg bg-white text-stone-800 focus:ring-2 focus:ring-rose-500 focus:border-rose-500';

/**
 * One logical role. The role — not the model — carries burnRate and
 * minCreditsPerCall, which is what makes a vendor swap invisible to the
 * tenant's bill. The card says that on screen so the property survives the
 * next operator who did not read the spec.
 */
const RoutingRoleCard: React.FC<Props> = ({
  roleId, original, draft, providers, models, saving, onChange, onSave, onReset,
}) => {
  const [showFallback, setShowFallback] = useState(!!draft.fallbackModelId);

  const errors = useMemo(
    () => validateRoleDraft(draft, models, providers),
    [draft, models, providers],
  );
  const diff = useMemo(() => roleDiff(original, draft), [original, draft]);
  const dirty = diff.length > 0;

  const set = (patch: Partial<RoleDraft>) => onChange({ ...draft, ...patch });

  return (
    <div className={`bg-white border rounded-xl shadow-sm overflow-hidden ${
      dirty ? 'border-rose-300 ring-1 ring-rose-100' : 'border-stone-200'
    }`}>
      <div className="px-4 py-3 border-b border-stone-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            {AI_ROLE_LABELS[roleId]}
            <span className="text-[10px] font-mono text-gray-400">{roleId}</span>
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">{AI_ROLE_HINTS[roleId]}</p>
        </div>
        <button
          type="button"
          onClick={() => set({ enabled: !draft.enabled })}
          aria-pressed={draft.enabled}
          aria-label={`${draft.enabled ? 'Disable' : 'Enable'} ${AI_ROLE_LABELS[roleId]}`}
          className="relative inline-flex w-11 flex-shrink-0 items-center justify-center rounded-full transition-colors min-h-[44px] bg-transparent px-0"
        >
          <span className={`inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            draft.enabled ? 'bg-emerald-500' : 'bg-stone-300'
          }`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              draft.enabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </span>
        </button>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <span className={FIELD_LABEL}>Provider</span>
            <SearchableDropdown
              options={providerOptions(providers)}
              value={draft.providerId || null}
              onChange={(v) => set({ providerId: v || '', modelId: '' })}
              placeholder="Choose a provider"
              searchPlaceholder="Search providers…"
              disabled={saving}
            />
          </div>
          <div>
            <span className={FIELD_LABEL}>Model</span>
            <SearchableDropdown
              options={modelOptions(models, draft.providerId)}
              value={draft.modelId || null}
              onChange={(v) => set({ modelId: v || '' })}
              placeholder={draft.providerId ? 'Choose a model' : 'Choose a provider first'}
              searchPlaceholder="Search models…"
              disabled={saving || !draft.providerId}
            />
          </div>
        </div>

        <div>
          <button
            type="button"
            onClick={() => {
              const next = !showFallback;
              setShowFallback(next);
              if (!next) set({ fallbackProviderId: '', fallbackModelId: '' });
            }}
            className="text-[11px] font-semibold text-rose-600 hover:text-rose-700 min-h-[44px]"
          >
            {showFallback ? 'Remove fallback' : 'Add a fallback model'}
          </button>
          <p className="text-[11px] text-gray-500">
            Used when the primary model or its provider is disabled. If both are
            unavailable the call fails loudly — it never downgrades to an unpriced model.
          </p>
          {showFallback && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              <div>
                <span className={FIELD_LABEL}>Fallback provider</span>
                <SearchableDropdown
                  options={providerOptions(providers)}
                  value={draft.fallbackProviderId || null}
                  onChange={(v) => set({ fallbackProviderId: v || '', fallbackModelId: '' })}
                  placeholder="Choose a provider"
                  searchPlaceholder="Search providers…"
                  disabled={saving}
                />
              </div>
              <div>
                <span className={FIELD_LABEL}>Fallback model</span>
                <SearchableDropdown
                  options={modelOptions(models, draft.fallbackProviderId)}
                  value={draft.fallbackModelId || null}
                  onChange={(v) => set({ fallbackModelId: v || '' })}
                  placeholder={draft.fallbackProviderId ? 'Choose a model' : 'Choose a provider first'}
                  searchPlaceholder="Search models…"
                  disabled={saving || !draft.fallbackProviderId}
                />
              </div>
            </div>
          )}
        </div>

        {/* Tenant-facing knobs. Called out as such on screen. */}
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
            What the tenant is charged
          </p>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className={FIELD_LABEL}>Burn rate</span>
              <input
                type="number" step="0.1" min="0" max="50" inputMode="decimal"
                value={draft.burnRate}
                onChange={(e) => set({ burnRate: parseFloat(e.target.value) })}
                disabled={saving}
                className={NUM_INPUT}
              />
            </label>
            <label className="block">
              <span className={FIELD_LABEL}>Min credits / call</span>
              <input
                type="number" step="1" min="0" inputMode="numeric"
                value={draft.minCreditsPerCall}
                onChange={(e) => set({ minCreditsPerCall: parseInt(e.target.value, 10) })}
                disabled={saving}
                className={NUM_INPUT}
              />
            </label>
            <label className="block">
              <span className={FIELD_LABEL}>Reserve cap</span>
              <input
                type="number" step="1" min="1" inputMode="numeric"
                value={draft.reserveCap}
                onChange={(e) => set({ reserveCap: parseInt(e.target.value, 10) })}
                disabled={saving}
                className={NUM_INPUT}
              />
            </label>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">
            These live on the role, not on the model. Swapping the model above
            changes what the call costs <strong>us</strong> and leaves what the
            tenant pays exactly as it is.
          </p>
        </div>

        {errors.length > 0 && (
          <ul className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 space-y-1">
            {errors.map((e) => (
              <li key={e} className="text-[11px] text-rose-700 flex items-start gap-1.5">
                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-4 py-3 border-t border-stone-100 flex items-center justify-between gap-2">
        <span className="text-[11px] text-gray-500">
          {dirty
            ? `${diff.length} unsaved change${diff.length === 1 ? '' : 's'}`
            : 'No changes'}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-xs font-medium text-stone-600 rounded-lg hover:bg-stone-100 disabled:opacity-40"
          >
            <RotateCcw size={14} /> Reset
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!dirty || saving || errors.length > 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] text-xs font-semibold bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-40"
          >
            {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />}
            Save role
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoutingRoleCard;
